'use client';

import { useTranslations } from 'next-intl';
import type { Price } from '@cobrastyle/shared-types';
import type { PaketItem } from '@/lib/paket';
import { formatPrice } from '@/lib/format-price';

interface PaketIncludesListProps {
  items: PaketItem[];
  /** The package's own price — the only figure this module prints. */
  price: Price;
}

/**
 * The buy box's contents list, footed by the package price.
 *
 * Lists the same children as the grid on purpose: the canvas draws both, and
 * they answer different questions. The grid is the thing you click; this is the
 * receipt you read beside the price while deciding.
 *
 * The canvas gave each row the child's own price. That column is deliberately
 * gone: the parts of a paket do not reliably sum to the package price — on
 * 9010108 they come to 37 395 kr against a package price of 37 900 kr — so a
 * priced list footed by "Paketpris" invites the reader to check arithmetic that
 * does not add up. Without it this is the same names-and-✓ list the canvas's own
 * mobile artboard shows.
 */
export function PaketIncludesList({ items, price }: PaketIncludesListProps) {
  const t = useTranslations('product');

  if (items.length === 0) return null;

  return (
    <div className="overflow-hidden rounded-[3px] border border-border">
      {items.map((item) => (
        <div
          key={item.sku}
          className="flex items-center justify-between gap-2.5 border-b border-border bg-surface-subtle px-[18px] py-3"
        >
          {/* The name alone. The canvas prefixed the brand, but its fixture
              names omitted it — Torsbo's real ones lead with it, so prefixing
              printed "Sauer Kulgevär Sauer 101 Classic XTA". */}
          <span className="text-[13.5px] text-foreground/80">{item.name}</span>
          <span aria-hidden className="flex-none text-success-600">
            ✓
          </span>
        </div>
      ))}

      <div className="flex items-center justify-between bg-card px-[18px] py-3">
        <span className="text-[13.5px] font-bold text-heading">{t('paketPrice')}</span>
        <span className="text-[13.5px] font-bold text-accent-700">
          {formatPrice(price.final, price.currency)}
        </span>
      </div>
    </div>
  );
}
